// Which mode the console opens in (docs/design/simple-mode.md §6): a browser
// preference, not a server field. Storage may be missing or throw (private
// window, blocked site data) - every access is guarded and falls back to MCR.

import type { Role } from '@/api/types'
import { roleAtLeast } from '@/api/access'

export type ConsoleMode = 'mcr' | 'simple'

export const MODE_STORAGE_KEY = 'airgate.console.mode'

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

function defaultStorage(): StorageLike | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

export function preferredMode(storage: StorageLike | null = defaultStorage()): ConsoleMode {
  try {
    return storage?.getItem(MODE_STORAGE_KEY) === 'simple' ? 'simple' : 'mcr'
  } catch {
    return 'mcr'
  }
}

/** Stores the preference; false when the browser refused (the choice then lasts only this page). */
export function setPreferredMode(mode: ConsoleMode, storage: StorageLike | null = defaultStorage()): boolean {
  try {
    if (!storage) return false
    storage.setItem(MODE_STORAGE_KEY, mode)
    return true
  } catch {
    return false
  }
}

/** Route name the console's home ("/") should open. */
export function homeRouteName(storage: StorageLike | null = defaultStorage()): 'simple' | 'overview' {
  return preferredMode(storage) === 'simple' ? 'simple' : 'overview'
}

// ---- basic / advanced menu view (docs/design/ux-simplify.md §3.1, Б2) ---------
//
// The advanced view lists the engineering screens too (lines, routes, ingest
// points, egress profiles, sites, the journal, the simple-mode list). Same
// rules as the mode above: a browser preference, guarded storage. With
// nothing stored the role decides: engineers and admins start advanced,
// operators and viewers basic.

export type ConsoleView = 'basic' | 'advanced'

export const VIEW_STORAGE_KEY = 'airgate.console.view'

/** The view a role starts with when this browser has no choice stored. */
export function defaultView(role: Role | null | undefined): ConsoleView {
  return roleAtLeast(role, 'engineer') ? 'advanced' : 'basic'
}

/** The stored choice, or null when there is none (or storage is unreadable). */
export function storedView(storage: StorageLike | null = defaultStorage()): ConsoleView | null {
  try {
    const v = storage?.getItem(VIEW_STORAGE_KEY)
    return v === 'basic' || v === 'advanced' ? v : null
  } catch {
    return null
  }
}

/** Stores the choice; false when the browser refused (it then lasts only this page). */
export function setStoredView(view: ConsoleView, storage: StorageLike | null = defaultStorage()): boolean {
  try {
    if (!storage) return false
    storage.setItem(VIEW_STORAGE_KEY, view)
    return true
  } catch {
    return false
  }
}
